import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Trophy, MessageSquare, Loader2, Check } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useMyChallenges } from '../lib/queries';
import { createPost } from '../lib/api';
import SendToChatModal from './SendToChatModal';

/**
 * Bottom-sheet modal that lets the user share an AI-generated recipe
 * to the public feed, to one or more of their active challenges, or
 * both. Mirrors the UX of `ShareWeightModal` for consistency.
 *
 * Caller passes the already-built post payload (text + imageUrl + data)
 * so we don't have to duplicate the recipe-formatting logic here.
 */
export default function ShareRecipeModal({
  isOpen,
  onClose,
  isDark,
  recipe,
  imageUrl,
  text, // fully formatted post text from the parent
  onPublished,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [busy, setBusy] = useState(null); // 'feed' | 'challenges'
  const [showChallengePicker, setShowChallengePicker] = useState(false);
  const [showChatPicker, setShowChatPicker] = useState(false);
  const [selectedChallengeIds, setSelectedChallengeIds] = useState([]);
  const [toast, setToast] = useState(null);

  const { data: myChallenges = [] } = useMyChallenges();
  const eligibleChallenges = (myChallenges || []).filter(
    (c) => c.status !== 'ended' && (c.participants || []).some((p) => p.user_id === user?.user_id)
  );

  useEffect(() => {
    if (!isOpen) {
      setBusy(null);
      setShowChallengePicker(false);
      setSelectedChallengeIds([]);
      setToast(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2200);
  };

  const handleShareToFeed = async () => {
    setBusy('feed');
    try {
      await createPost({
        post_type: 'pasto',
        text,
        image_url: imageUrl,
        data: { recipe, is_ai_generated: true },
      });
      showToast(t('weightShare.publishedFeed', 'Pubblicato nel feed!'));
      setTimeout(() => { onPublished?.(); onClose(); }, 900);
    } catch (err) {
      console.error(err);
      showToast(t('weightShare.error', 'Errore durante la condivisione'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const handleShareToChallenges = async () => {
    if (selectedChallengeIds.length === 0) return;
    setBusy('challenges');
    try {
      await createPost({
        post_type: 'pasto',
        text,
        image_url: imageUrl,
        data: { recipe, is_ai_generated: true },
        challenge_ids: selectedChallengeIds,
      });
      showToast(t('weightShare.publishedChallenges', 'Pubblicato nelle sfide!'));
      setTimeout(() => { onPublished?.(); onClose(); }, 900);
    } catch (err) {
      console.error(err);
      showToast(t('weightShare.error', 'Errore durante la condivisione'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const toggleChallenge = (cid) => {
    setSelectedChallengeIds((prev) =>
      prev.includes(cid) ? prev.filter((x) => x !== cid) : [...prev, cid]
    );
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      style={{ zIndex: 9999 }}
      onClick={() => !busy && onClose()}
      data-testid="share-recipe-modal"
    >
      <div
        className={`w-full max-w-md rounded-t-3xl p-6 animate-slide-up ${isDark ? 'bg-[#0D1424]' : 'bg-white'}`}
        style={{ paddingBottom: 'calc(28px + env(safe-area-inset-bottom, 0px))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <h2 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {t('weightShare.title', 'Condividi')}
            </h2>
            <p className={`text-xs mt-1 truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
              {recipe?.nome}
            </p>
          </div>
          <button
            onClick={() => !busy && onClose()}
            data-testid="share-recipe-close-btn"
            className={`w-10 h-10 rounded-full flex items-center justify-center ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}
          >
            <X className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
          </button>
        </div>

        {!showChallengePicker ? (
          <div className="space-y-3">
            <ShareAction
              icon={<Send className="w-5 h-5" />}
              color="#16A34A"
              label={t('weightShare.publishToFeed', 'Pubblica nel feed')}
              desc={t('weightShare.publishToFeedDesc', 'Visibile a tutta la community')}
              isDark={isDark}
              loading={busy === 'feed'}
              disabled={!!busy}
              onClick={handleShareToFeed}
              testid="share-recipe-feed-btn"
            />
            <ShareAction
              icon={<Trophy className="w-5 h-5" />}
              color="#F59E0B"
              label={t('weightShare.publishToChallenges', 'Pubblica in una sfida')}
              desc={
                eligibleChallenges.length > 0
                  ? t('weightShare.publishToChallengesDesc', 'Seleziona una o più sfide')
                  : t('weightShare.noChallenges', 'Nessuna sfida attiva')
              }
              isDark={isDark}
              disabled={!!busy || eligibleChallenges.length === 0}
              onClick={() => setShowChallengePicker(true)}
              testid="share-recipe-challenges-btn"
            />
            <ShareAction
              icon={<MessageSquare className="w-5 h-5" />}
              color="#3B82F6"
              label={t('messages.sendToChat', 'Invia in chat')}
              desc={t(
                'messages.sendToChatDesc',
                'Condividi con un amico in privato'
              )}
              isDark={isDark}
              disabled={!!busy}
              onClick={() => setShowChatPicker(true)}
              testid="share-recipe-chat-btn"
            />
          </div>
        ) : (
          <div>
            <p className={`text-sm mb-3 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              {t('weightShare.selectChallenges', 'Seleziona una o più sfide:')}
            </p>
            <div className="max-h-[40vh] overflow-y-auto -mx-1 px-1 mb-4 space-y-2">
              {eligibleChallenges.map((c) => {
                const checked = selectedChallengeIds.includes(c.challenge_id);
                return (
                  <button
                    key={c.challenge_id}
                    onClick={() => toggleChallenge(c.challenge_id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                      checked
                        ? isDark
                          ? 'bg-[#E8334A]/20 border-2 border-[#E8334A]'
                          : 'bg-[#E8334A]/10 border-2 border-[#E8334A]'
                        : isDark
                          ? 'bg-white/5 border-2 border-transparent'
                          : 'bg-gray-100 border-2 border-transparent'
                    }`}
                    data-testid={`share-recipe-challenge-${c.challenge_id}`}
                  >
                    <div
                      className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                        checked ? 'bg-[#E8334A]' : isDark ? 'bg-white/10' : 'bg-white'
                      }`}
                      style={{ border: checked ? 'none' : '1.5px solid #94a3b8' }}
                    >
                      {checked && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {c.emoji ? `${c.emoji} ` : ''}{c.name}
                      </div>
                      <div className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                        {(c.participants || []).length} {t('challenges.participants', 'partecipanti')}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowChallengePicker(false)}
                disabled={!!busy}
                className={`flex-1 py-3 rounded-xl text-sm font-semibold ${isDark ? 'bg-white/10 text-white' : 'bg-gray-100 text-gray-700'}`}
                data-testid="share-recipe-back-btn"
              >
                {t('common.back', 'Indietro')}
              </button>
              <button
                onClick={handleShareToChallenges}
                disabled={busy === 'challenges' || selectedChallengeIds.length === 0}
                data-testid="share-recipe-publish-challenges-btn"
                className={`flex-1 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 ${
                  selectedChallengeIds.length > 0 && busy !== 'challenges'
                    ? 'bg-[#E8334A] text-white'
                    : 'bg-[#E8334A]/40 text-white/70 cursor-not-allowed'
                }`}
              >
                {busy === 'challenges' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    {t('weightShare.publishCount', 'Pubblica')}
                    {selectedChallengeIds.length > 0 ? ` (${selectedChallengeIds.length})` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {toast && (
          <div className={`mt-4 rounded-xl px-4 py-3 text-sm text-center ${
            toast.type === 'error' ? 'bg-red-500/15 text-red-400' : 'bg-green-500/15 text-green-500'
          }`}>
            {toast.message}
          </div>
        )}

        {/* Inner picker: select a chat to share into. The recipe data
            is snapshot-ed so the bubble keeps working even if the
            recipe is later deleted by the author. */}
        <SendToChatModal
          isOpen={showChatPicker}
          onClose={() => setShowChatPicker(false)}
          type="recipe_share"
          data={{
            recipe_id: recipe?.recipe_id || null,
            nome: recipe?.nome || '',
            descrizione: recipe?.descrizione || '',
            image_url: imageUrl || null,
            calorie: recipe?.calorie || null,
            tempo_preparazione: recipe?.tempo_preparazione || null,
            proteine: recipe?.proteine || null,
            carboidrati: recipe?.carboidrati || null,
            grassi: recipe?.grassi || null,
          }}
          caption={`🍳 ${recipe?.nome || ''}`}
        />
      </div>
    </div>,
    document.body
  );
}

function ShareAction({ icon, color, label, desc, isDark, onClick, disabled, loading, testid }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`w-full flex items-center gap-4 p-4 rounded-2xl text-left transition-opacity ${
        isDark ? 'bg-white/5' : 'bg-gray-50'
      } ${disabled ? 'opacity-50' : 'active:scale-[0.98]'}`}
    >
      <div
        className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 text-white"
        style={{ background: color }}
      >
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{label}</div>
        <div className={`text-xs mt-0.5 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{desc}</div>
      </div>
    </button>
  );
}
